import { useCallback, useEffect, useRef } from 'react'

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/**
 * Cloudflare Turnstile site key (public). Sourced from the environment so each
 * environment targets the correct widget; set `VITE_TURNSTILE_SITE_KEY` in every
 * deployment and in `.env` for local development (see `.env.example`).
 */
export const TURNSTILE_SITE_KEY: string =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''

/** Telemetry marker so Cloudflare can attribute Spin-managed deployments. */
export const TURNSTILE_ACTION = 'turnstile-spin-v1'

interface TurnstileRenderOptions {
  sitekey: string
  action?: string
  callback?: (token: string) => void
  'error-callback'?: () => void
  'expired-callback'?: () => void
  'timeout-callback'?: () => void
  execution?: 'render' | 'execute'
  appearance?: 'always' | 'execute' | 'interaction-only'
  theme?: 'light' | 'dark' | 'auto'
}

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: TurnstileRenderOptions) => string
      execute: (
        el: HTMLElement | string,
        opts?: Partial<TurnstileRenderOptions>
      ) => void
      reset: (widgetId?: string) => void
      remove: (widgetId: string) => void
    }
  }
}

let scriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`
    )
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        reject(new Error('turnstile_script_failed'))
      )
      if (window.turnstile) resolve()
      return
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () =>
      reject(new Error('turnstile_script_failed'))
    )
    document.head.appendChild(script)
  })
  return scriptPromise
}

export interface UseTurnstileResult {
  /** Executes the invisible widget and resolves with a fresh, single-use token. */
  getToken: () => Promise<string>
  /** Clears the current solve so the next `getToken()` produces a fresh token. */
  reset: () => void
}

/**
 * Renders one invisible Turnstile widget for the lifetime of the calling
 * component and exposes an imperative `getToken()` that resolves with a fresh,
 * single-use token. Built for gating specific API calls (e.g. `/start`,
 * `/phone/send-code`) rather than a visible form-submit button.
 */
export function useTurnstile(action: string = TURNSTILE_ACTION): UseTurnstileResult {
  const widgetIdRef = useRef<string | null>(null)
  const pendingRef = useRef<{
    resolve: (token: string) => void
    reject: (error: Error) => void
  } | null>(null)
  const hasSolvedRef = useRef(false)
  const readyRef = useRef<Promise<void> | null>(null)

  useEffect(() => {
    let cancelled = false
    const container = document.createElement('div')
    document.body.appendChild(container)

    readyRef.current = loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          execution: 'execute',
          appearance: 'interaction-only',
          callback: (token) => {
            hasSolvedRef.current = true
            pendingRef.current?.resolve(token)
            pendingRef.current = null
          },
          'error-callback': () => {
            pendingRef.current?.reject(new Error('turnstile_error'))
            pendingRef.current = null
          },
          'expired-callback': () => {
            hasSolvedRef.current = false
          },
          'timeout-callback': () => {
            pendingRef.current?.reject(new Error('turnstile_timeout'))
            pendingRef.current = null
          },
        })
      })
      .catch((error: unknown) => {
        pendingRef.current?.reject(
          error instanceof Error ? error : new Error('turnstile_load_failed')
        )
        pendingRef.current = null
      })

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
      container.remove()
      pendingRef.current = null
    }
  }, [action])

  const reset = useCallback(() => {
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current)
      hasSolvedRef.current = false
    }
  }, [])

  const getToken = useCallback(async (): Promise<string> => {
    if (readyRef.current) await readyRef.current
    if (!window.turnstile || !widgetIdRef.current) {
      throw new Error('turnstile_not_ready')
    }
    // A previous solve may still be awaiting its callback; reject it so its
    // caller isn't left with a promise that never settles once we supersede it.
    if (pendingRef.current) {
      pendingRef.current.reject(new Error('turnstile_superseded'))
      pendingRef.current = null
    }
    // Tokens are single-use; clear a prior solve before requesting a new one.
    if (hasSolvedRef.current) {
      window.turnstile.reset(widgetIdRef.current)
      hasSolvedRef.current = false
    }
    return new Promise<string>((resolve, reject) => {
      pendingRef.current = { resolve, reject }
      try {
        window.turnstile!.execute(widgetIdRef.current!, { action })
      } catch (error) {
        pendingRef.current = null
        reject(
          error instanceof Error ? error : new Error('turnstile_execute_failed')
        )
      }
    })
  }, [action])

  return { getToken, reset }
}
